/**
 * TextSizeToggle — a segmented control that lets a reader switch the
 * degree page between "Compact" (native/default) and "Comfortable"
 * (visibly larger) text. Designed for parents / older readers whose
 * eyes strain against the micro-text used across data-dense chart
 * cards, so **Comfortable is the default** — a fresh visitor with no
 * saved preference lands on the larger scale.
 *
 * State is lifted to DegreeDetail (which owns the `data-text-size`
 * attribute on the top-level wrapper) and persisted to localStorage so
 * the preference survives navigation and reloads.
 */
import { Type } from "lucide-react";

export const TEXT_SIZE_KEY = "wua.degreePage.textSize";

export function loadTextSize() {
  try {
    const v = localStorage.getItem(TEXT_SIZE_KEY);
    // No saved preference → default to "comfortable" so parents /
    // older readers land on the larger scale without hunting for the
    // toggle. Only an explicit "compact" flips it back down.
    if (v === "compact") return "compact";
    return "comfortable";
  } catch {
    return "comfortable";
  }
}

export function saveTextSize(v) {
  try { localStorage.setItem(TEXT_SIZE_KEY, v); } catch { /* ignore */ }
}

function Segment({ label, isActive, onClick, testKey }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`text-size-segment-${testKey}`}
      aria-pressed={isActive}
      className={
        "px-3 py-1.5 text-[11px] font-underground-sc tracking-[0.14em] " +
        "rounded-full transition-colors " +
        (isActive
          ? "bg-[#4C1D95] text-white shadow-sm"
          : "text-stone-500 hover:text-stone-800")
      }
    >
      {label}
    </button>
  );
}

export default function TextSizeToggle({ value, onChange }) {
  const set = (next) => {
    onChange?.(next);
    saveTextSize(next);
  };
  return (
    <div
      data-testid="text-size-toggle"
      role="group"
      aria-label="Text size"
      className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white/70 backdrop-blur px-1 py-1"
    >
      <Type className="h-3 w-3 text-stone-500 ml-1.5 mr-0.5" aria-hidden />
      <Segment
        label="Compact"
        testKey="compact"
        isActive={value === "compact"}
        onClick={() => set("compact")}
      />
      <Segment
        label="Comfortable"
        testKey="comfortable"
        isActive={value !== "compact"}
        onClick={() => set("comfortable")}
      />
    </div>
  );
}
